import { useMemo } from 'react';
import { useTenantConfig } from './useTenantConfig';
import {
  DATE_RANGE_OPTIONS,
  FISCAL_DATE_RANGE_OPTIONS,
  mapDateRangeFromBackend,
} from '@/components/ui/date-range-picker';
import type { ComboboxOption } from '@/components/ui/combobox';

/**
 * Returns the filtered list of date range options based on the tenant config whitelist.
 * If the tenant has no whitelist (null/empty), all options are returned unfiltered.
 *
 * The whitelist is stored in backend format (uppercase, e.g. "THIS_YEAR"),
 * so we convert to frontend format for comparison.
 */
export function useAvailableDashboardDateRangeOptions(showFiscalOptions = false): ComboboxOption[] {
  const { data: tenantConfig } = useTenantConfig();

  return useMemo(() => {
    const allStandard = DATE_RANGE_OPTIONS;
    const allFiscal = showFiscalOptions ? FISCAL_DATE_RANGE_OPTIONS : [];
    const allOptions = [...allStandard, ...allFiscal];

    const whitelist = tenantConfig?.dashboard_date_range_options;
    if (!whitelist || whitelist.length === 0) {
      return allOptions;
    }

    // Convert backend format whitelist to frontend format for matching
    const allowedFrontend = new Set(
      whitelist.map((v) => mapDateRangeFromBackend(v)).filter(Boolean) as string[]
    );

    return allOptions.filter((opt) => allowedFrontend.has(opt.value));
  }, [tenantConfig?.dashboard_date_range_options, showFiscalOptions]);
}
